import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useVersion } from 'react-admin';
import { Box, Button, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { UserDto } from '@vatos-pas/common';

import { SnackbarContainer } from 'components/standalone';
import { extractErrorMessage } from 'utils';

import { ContractorForm } from '../components';
import {
  BumperOption,
  ContractorContainer,
  ContractorFormValues,
} from '../containers';
import { PermissionsProvider } from 'providers/permissionsProvider';
import {
  CAN_EDIT_CONTRACTOR_CAPACITY,
  CAN_EDIT_CONTRACTOR_DETAILS,
} from 'providers/permissions';
import { getMissingPaymentTypeMessage } from '../resource-types';
import {
  BUMPERS_STILL_ASSIGNED_MESSAGE,
  buildBumperUsersDelta,
  buildContractorCapacityOnlyPayload,
  buildContractorPayload,
} from './contractorPayload';

type RoundTripLocationState = {
  formState?: Partial<ContractorFormValues>;
  selectedBumpers?: string[];
  user?: UserDto;
};

const ContractorsEditInner = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { notify } = SnackbarContainer.useContainer();
  const { hasPermission } = PermissionsProvider.useContainer();
  // Two-tier permission: DETAILS gates name/payments/regions/notes/toggles;
  // CAPACITY gates the resource-type table (Regional Manager profile).
  const cannotEditDetails = !hasPermission(CAN_EDIT_CONTRACTOR_DETAILS);
  const canEditCapacity = hasPermission(CAN_EDIT_CONTRACTOR_CAPACITY);
  // If the user has neither, they shouldn't be on this screen at all — but
  // belt-and-suspenders: block submit so we don't ship empty PATCHes.
  const canSubmit = !cannotEditDetails || canEditCapacity;
  const {
    selectedContractor,
    isLoading,
    contractorLoaded,
    contractorError,
    formLoading,
    contractorRegions,
    contractorUsers,
    contractorRegionsLoaded,
    contractorUsersLoaded,
    regionsError,
    bumpersError,
    loadContractorById,
    updateContractor,
    loadRegionOptions,
    loadBumperOptions,
    loadContractorRegionsForContractor,
    loadContractorUsersForContractor,
    setFormLoading,
    resetLoadedFlags,
  } = ContractorContainer.useContainer();

  const version = useVersion();

  const contractorId = selectedContractor?.id;

  const routerState = location.state as RoundTripLocationState | undefined;
  const roundTripFormState = routerState?.formState ?? null;
  const roundTripUser = routerState?.user ?? null;

  const roundTripSelectedBumpers = useMemo<string[] | null>(() => {
    if (!routerState) return null;
    const base = routerState.selectedBumpers ?? [];
    if (!roundTripUser) return base;
    return Array.from(new Set([...base, roundTripUser.id]));
  }, [routerState, roundTripUser]);

  const roundTripBumperOption = useMemo<BumperOption | null>(() => {
    if (!roundTripUser) return null;
    return {
      id: roundTripUser.id,
      name: `${roundTripUser.firstName} ${roundTripUser.lastName}`,
    };
  }, [roundTripUser]);

  useEffect(() => {
    if (!id) return;
    loadContractorById(id);
  }, [id, version, loadContractorById]);

  useEffect(() => {
    if (!contractorId) return;
    resetLoadedFlags();
    loadContractorRegionsForContractor(contractorId);
    loadContractorUsersForContractor(contractorId);
  }, [
    contractorId,
    resetLoadedFlags,
    loadContractorRegionsForContractor,
    loadContractorUsersForContractor,
  ]);

  useEffect(() => {
    if (contractorError || (contractorLoaded && !selectedContractor)) {
      notify('Contractor not found', 'error');
      history.push('/contractor');
    }
  }, [contractorError, contractorLoaded, selectedContractor, notify, history]);

  const initialRegionIds = useMemo(
    () => contractorRegions.map(cr => cr.regionId),
    [contractorRegions],
  );

  const initialBumperIds = useMemo(
    () => contractorUsers.map(cu => cu.userId),
    [contractorUsers],
  );

  const regionsLoading = !!contractorId && !contractorRegionsLoaded;
  const bumpersLoading = !!contractorId && !contractorUsersLoaded;

  const handleSubmit = useCallback(
    async (values: ContractorFormValues) => {
      if (!id) return;
      if (!canSubmit) return;

      // Guarded toggle (e.g. Bumper) ON without its payment type → block & warn.
      const missingPaymentMessage = getMissingPaymentTypeMessage(
        values as unknown as Record<string, unknown>,
      );
      if (missingPaymentMessage) {
        notify(missingPaymentMessage, 'error');
        return;
      }

      // Bumper OFF while bumpers are still selected: the field is hidden
      // when the toggle is off, so the user cannot see what would be
      // unlinked. Removing them is an explicit step (the API refuses the
      // transition too).
      if (
        !cannotEditDetails &&
        !values.bumperAvailable &&
        values.selectedBumpers.length > 0
      ) {
        notify(BUMPERS_STILL_ASSIGNED_MESSAGE, 'error');
        return;
      }

      setFormLoading(true);
      try {
        // Regional Manager (capacity only): only resource-type toggles +
        // capacities are sent and no relation set; the API enforces the same
        // cut server-side (PATCH /contractor/:id/full, updateOwn path).
        // Single transactional request either way.
        if (cannotEditDetails) {
          await updateContractor(id, buildContractorCapacityOnlyPayload(values));
        } else {
          await updateContractor(id, buildContractorPayload(values), {
            regionIds: values.selectedRegions,
            // Only the links the user actually added or removed travel;
            // undefined when unchanged keeps them untouched server-side.
            bumperUsers: buildBumperUsersDelta(
              initialBumperIds,
              values.selectedBumpers,
            ),
          });
        }

        notify('Element updated');
        setFormLoading(false);
        history.push('/contractor');
      } catch (error: unknown) {
        console.error('Contractor update failed:', error);
        notify(
          extractErrorMessage(error, 'Error while updating the contractor'),
          'error',
        );
        setFormLoading(false);
      }
    },
    [
      id,
      canSubmit,
      cannotEditDetails,
      initialBumperIds,
      updateContractor,
      history,
      notify,
      setFormLoading,
    ],
  );

  const handleGoBack = useCallback(() => {
    history.push('/contractor');
  }, [history]);

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedContractor) {
    return null;
  }

  return (
    <Box className={classes.formContainer}>
      <Box className={classes.toolbar}>
        <Button
          color="primary"
          variant="text"
          data-cy="contractorEdit-showButton"
          onClick={() => history.push(`/contractor/${id}/show`)}
        >
          Show
        </Button>
      </Box>
      <ContractorForm
        mode="edit"
        initialValues={selectedContractor}
        initialRegionIds={initialRegionIds}
        initialBumperIds={initialBumperIds}
        roundTripFormState={roundTripFormState}
        roundTripSelectedBumpers={roundTripSelectedBumpers}
        roundTripBumperOption={roundTripBumperOption}
        onSubmit={handleSubmit}
        // Saving with a half-loaded region list would send an incomplete
        // desired set and unlink regions, and the bumper delta needs the
        // initial links, so the form stays disabled until both lists are in.
        loading={formLoading || regionsLoading || bumpersLoading}
        onGoBack={handleGoBack}
        loadRegionOptions={loadRegionOptions}
        loadBumperOptions={loadBumperOptions}
        regionsLoading={regionsLoading}
        bumpersLoading={bumpersLoading}
        regionsError={regionsError}
        bumpersError={bumpersError}
        cannotEditDetails={cannotEditDetails}
      />
    </Box>
  );
};

export const ContractorsEdit = () => (
  <ContractorContainer.Provider>
    <ContractorsEditInner />
  </ContractorContainer.Provider>
);

const useStyles = makeStyles(theme => ({
  formContainer: {
    position: 'relative',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
}));

export default ContractorsEdit;
